/* eslint-disable max-lines */
import { Article, ArticleResolution, ArticleType } from '@wix/answers-api';
import { getTopOffsetForScrollToArticlePage } from '@wix/cc-user-facing-common-components';
import { Box } from '@wix/design-system';
import { componentsToPlainText } from '@wix/kb-article-viewer';
import classNames from 'classnames';
import Long from 'long';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { animateScroll, scroller } from 'react-scroll';

import { ArticleFooter } from './ArticleFooter';
import { ArticleHeader } from './ArticleHeader';
import { ArticleViewerWrapper } from './ArticleViewerWrapper';
import { useArticleContentBi } from './bi';
import { CopilotButton } from './CopilotButton';
import { TopicsButton } from './TopicsButton';
import { WrongArticle } from './WrongArticle';
import { getWrongArticleDescription } from './WrongArticle/wrongArticleData';
import { BI, LOCALES } from '../../constants';
import { useChatbotMessageCounter } from '../../hooks/useChatbotMessageCounter';
import useIsArticlePageRedesignUnifiedUXEnabled from '../../hooks/useIsArticlePageRedesignUnifiedUXEnabled';
import useIsHelpCenterCopilotEnabled from '../../hooks/useIsHelpCenterCopilotEnabled';
import useIsHelpmateImprovedFeedbackEnabled from '../../hooks/useIsHelpmateImprovedFeedbackEnabled';
import { useMaxScrollPercentage } from '../../hooks/useMaxScrollPercentage';
import { useResponsive } from '../../hooks/useResponsive';
import { useWindowSize } from '../../hooks/useWindowSize';
import useYouTubePlayers from '../../hooks/useYouTubePlayers';
import { Step } from '../../pages/article/[articleTitle]';
import { normalizeArticle } from '../../services/utils/article';
import { ArticleWithComponents, PanelAction, PanelType } from '../../types';
import { useArticlePageBi } from '../../utils/article-page-bi';
import { useStore } from '../../zustand/stores';
import { CopilotDisplayMode } from '../../zustand/stores/copilot';
import { ArticleFeedback } from '../ArticleFeedback';
import { initChatFlowForWrongArticle } from '../ArticleRightSideBar/iframeMessageHandler';
import { BreadcrumbItem, Breadcrumbs } from '../Breadcrumbs';
const Loader = dynamic(() => import('../Loader'), { ssr: false });

import css from './index.module.scss';

const WRONG_ARTICLE_CHATBOT_NODE_ID = '526e94e4-2ed8-41c0-ab15-ddda2601ea31';

type ArticleContentProps = {
  article: ArticleWithComponents;
  isRoadmapArticle?: boolean;
  readingTimeInMinutes?: number;
  onClickStatus?: (resolution: ArticleResolution) => void;
  relatedArticles: Article[];
  steps: Step[];
  showLeftSideBar: boolean;
  isLoading: boolean;
  setShowLeftSideBar: (showLeftSideBar: boolean) => void;
  showRightSideBar: boolean;
  setShowRightSideBar: (showRightSideBar: boolean) => void;
  breadcrumbItems: BreadcrumbItem[];
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string,
  ) => void;
};

// eslint-disable-next-line complexity
export const ArticleContent: FunctionComponent<ArticleContentProps> = ({
  article,
  isRoadmapArticle,
  onClickStatus,
  readingTimeInMinutes,
  relatedArticles,
  steps,
  showLeftSideBar,
  setShowLeftSideBar,
  showRightSideBar,
  setShowRightSideBar,
  breadcrumbItems,
  copilotArticleContentRef,
  isLoading,
  selfArticleRedirect,
}: ArticleContentProps) => {
  const {
    id: articleId,
    type,
    components,
    url: articleUrl,
    title,
    resolution,
    resolutionEta,
  } = article;
  const { locale, query } = useRouter();
  const {
    setCopilotDisplayMode,
    unreadMessagesCount,
    isChatbotReady,
    setIsChatbotInitializing,
    setChatbotNodeId,
  } = useStore((state) => state.copilotSlice);
  const containerRef = useRef<HTMLDivElement>(null);
  useYouTubePlayers(containerRef, articleId);
  const { onLinkClickBi } = useArticleContentBi(articleId, type);
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isArticlePageRedesignUnifiedUXEnabled = useIsArticlePageRedesignUnifiedUXEnabled();
  const isHelpmateImprovedFeedbackEnabled =
    useIsHelpmateImprovedFeedbackEnabled();
  const [maxScrollPercentage, isScrollable] = useMaxScrollPercentage(
    copilotArticleContentRef,
    articleId,
  );
  const { isMobile } = useResponsive();
  const windowSize = useWindowSize();
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(true);
  const shouldShowFeedback =
    (!isScrollable || maxScrollPercentage >= 30) && isFeedbackVisible;

  useEffect(() => {
    setIsFeedbackVisible(true);
  }, [article]);
  const showDisclaimerForFeatureRequest =
    type === ArticleType.FEATURE_REQUEST || isRoadmapArticle;
  const URL_TABS_SEPARATOR = '?tabs=';
  const URL_HEADING_SEPARATOR = ',';
  const isSSR = () => typeof window === 'undefined';
  const { panelTrack } = useArticlePageBi(article);
  useChatbotMessageCounter(articleId);

  const shouldOpenInNewTab = (e: MouseEvent): boolean =>
    (e.currentTarget as HTMLAnchorElement).target === '_blank' ||
    e.ctrlKey ||
    e.metaKey;

  const handleAnchorClick = (e: MouseEvent, hash: string) => {
    e.preventDefault();

    const targetElement = document.getElementById(hash);
    if (!targetElement) {
      return;
    }
    if (isHelpCenterCopilotEnabled && copilotArticleContentRef.current) {
      if (copilotArticleContentRef.current) {
        copilotArticleContentRef.current.scrollTo({
          top: targetElement.offsetTop - 5,
          behavior: 'smooth',
        });
      }
    } else {
      const boundingClientRect = targetElement.getBoundingClientRect();
      if (boundingClientRect) {
        animateScroll.scrollTo(
          boundingClientRect.top +
          window.scrollY -
          getTopOffsetForScrollToArticlePage(),
        );
      }
    }
    setTimeout(() => {
      location.hash = hash;
    }, 100);
  };

  const getAllOtherTabSlugs = (tabsList: string, currentTabSlugs: string[]) => {
    if (tabsList) {
      const unrelatedSlugs = tabsList
        .split(URL_HEADING_SEPARATOR)
        .map((slug) => decodeURI(slug))
        .filter((slug) => !currentTabSlugs.includes(slug));
      return unrelatedSlugs.length
        ? unrelatedSlugs.join(URL_HEADING_SEPARATOR) + URL_HEADING_SEPARATOR
        : '';
    }
    return '';
  };

  const onTabClick = async (selectedTab: number, currentTabSlugs: string[]) => {
    if (!isSSR()) {
      if (currentTabSlugs.length) {
        const hash = document.location.hash;
        const tabs = new URLSearchParams(document.location.search).get('tabs');
        const tabsSearchParams = tabs ? tabs.toString() : '';
        const allUnrelatedSlugsInURL = getAllOtherTabSlugs(
          tabsSearchParams,
          currentTabSlugs,
        );
        const selectedTabURLSlug = currentTabSlugs[selectedTab];
        const newURLState =
          `${location.pathname}${URL_TABS_SEPARATOR}${allUnrelatedSlugsInURL}` +
          `${selectedTabURLSlug}${hash}`;
        history.pushState(null, '', newURLState);
      }
    }
  };

  const maybeGetSelectedTabsFromURL = () => {
    if (!isSSR()) {
      const maybeURLTabs = query.tabs ? query.tabs.toString() : '';
      if (maybeURLTabs.length) {
        return maybeURLTabs
          .split(URL_HEADING_SEPARATOR)
          .map((slug) => decodeURI(slug));
      } else {
        return [];
      }
    }
    return [];
  };

  const internalAnchors = useCallback(() => {
    const allArticleLinks = document.querySelectorAll('a');
    return Array.from(allArticleLinks)
      .filter((link) => {
        const [linkWithoutHash, hash] = link.href.split('#');
        const sameArticle = decodeURI(linkWithoutHash) === articleUrl;
        return sameArticle && hash;
      })
      .reduce(
        (acc, link, index) => {
          const href = link.href;
          acc[href] = (index + 1).toString();
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [articleUrl]);

  const onLinkClick = async (e: MouseEvent, href: string) => {
    const target = e.target as HTMLElement;
    const text = target?.textContent as string;
    const {
      CUSTOM_HTML_ARTICLE_CTA_BUTTON,
      CUSTOM_HTML_ARTICLE_LOG_IN_BUTTON,
      CUSTOM_HTML_ARTICLE_GET_STARTED_BUTTON,
    } = BI.SELECTORS;

    const isButton = [
      CUSTOM_HTML_ARTICLE_CTA_BUTTON,
      CUSTOM_HTML_ARTICLE_LOG_IN_BUTTON,
      CUSTOM_HTML_ARTICLE_GET_STARTED_BUTTON,
    ].some((cta) => target.getAttribute('class')?.includes(cta));
    const [linkWithoutHash, hash] = href.split('#');
    const isOtherArticle = decodeURI(linkWithoutHash) !== articleUrl;
    let clickedItemType, clientSideNavigation, clickedItemOrder;
    if (isButton) {
      clickedItemType = BI.CLICKED_ITEM_TYPES.CTA;
    } else if (isOtherArticle) {
      if (isLinkToArticle(href, locale as string)) {
        if (!shouldOpenInNewTab(e)) {
          e.preventDefault();
          clientSideNavigation = true;
        } else {
          clientSideNavigation = false;
        }
        clickedItemType = BI.CLICKED_ITEM_TYPES.ARTICLE_LINK;
      } else {
        clickedItemType = BI.CLICKED_ITEM_TYPES.EXTERNAL_LINK;
      }
    } else {
      clickedItemType = BI.CLICKED_ITEM_TYPES.INTERNAL_ANCHOR;
      clickedItemOrder = internalAnchors()[href];
      handleAnchorClick(e, hash);
    }

    await onLinkClickBi(href, clickedItemType, text, clickedItemOrder);

    if (clientSideNavigation) {
      const { pathname, search, hash: hashSource } = new URL(href);
      const url = `${pathname}${search}${hashSource}`;
      let linkLocale: string | undefined = url.substring(1, 3);
      linkLocale = Object.values(LOCALES).includes(linkLocale)
        ? linkLocale
        : undefined;
      selfArticleRedirect(url, false, linkLocale);
    }
  };

  const containsContent = useMemo(
    () => componentsToPlainText(components).length > 0,
    [components],
  );

  useEffect(() => {
    if (copilotArticleContentRef.current) {
      copilotArticleContentRef.current.id = 'article-content-container';
    }
  }, []);

  useEffect(() => {
    const targetId = window.location.hash.substring(1);
    const targetElement = document.getElementById(targetId);

    const gotoAnchorPosition = () => {
      if (!targetElement) {
        return;
      }

      if (isHelpCenterCopilotEnabled) {
        scroller.scrollTo(targetId, {
          duration: 700,
          offset: -5,
          containerId: 'article-content-container',
          smooth: true,
        });
        setTimeout(() => {
          animateScroll.scrollToTop();
          scroller.scrollTo(targetId, {
            duration: 10,
            offset: -5,
            containerId: 'article-content-container',
            smooth: true,
          });
        }, 690);
      } else {
        const boundingClientRect = targetElement?.getBoundingClientRect();
        if (boundingClientRect) {
          animateScroll.scrollTo(
            targetElement.offsetTop - getTopOffsetForScrollToArticlePage(),
            {
              duration: 700,
              smooth: true,
            },
          );
        }
      }
    };
    gotoAnchorPosition();
  }, [articleId, isHelpCenterCopilotEnabled]);

  useEffect(() => {
    if (windowSize.width && windowSize.width < 1366) {
      if (showLeftSideBar && showRightSideBar) {
        setShowLeftSideBar(false);
      }
    }
  }, [setShowLeftSideBar, showLeftSideBar, showRightSideBar, windowSize]);

  const onTopicsButtonClick = async () => {
    if (windowSize.width && windowSize.width < 1366) {
      if (showRightSideBar) {
        setShowRightSideBar(false);
      }
    }
    await panelTrack(PanelType.ANCHORS, PanelAction.EXPAND);
    setShowLeftSideBar(true);
  };

  const onCopilotButtonClick = async () => {
    if (windowSize.width && windowSize.width < 1366) {
      if (showLeftSideBar) {
        setShowLeftSideBar(false);
      }
    }
    await panelTrack(PanelType.ASSISTANT, PanelAction.EXPAND);
    if (unreadMessagesCount > 0) {
      if (isChatbotReady) {
        setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
      } else {
        setIsChatbotInitializing(true);
      }
    }
    setShowRightSideBar(true);
  };

  const hideFeedbackAfterDelay = useCallback(() => {
    setTimeout(() => {
      setIsFeedbackVisible(false);
    }, 2000);
  }, []);

  const onWrongArticleFeedbackClick = useCallback(() => {
    setChatbotNodeId(WRONG_ARTICLE_CHATBOT_NODE_ID);
    if (!isChatbotReady) {
      setIsChatbotInitializing(true);
    } else {
      setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
      initChatFlowForWrongArticle(WRONG_ARTICLE_CHATBOT_NODE_ID);
    }

    setShowRightSideBar(true);
  }, [
    setIsChatbotInitializing,
    setShowRightSideBar,
    setChatbotNodeId,
    isChatbotReady,
  ]);

  const showOnlyLoader = isLoading && isArticlePageRedesignUnifiedUXEnabled;

  return article ? (
    <div
      className={classNames(css.wrapper, {
        [css.isCopilotEnabled]: isHelpCenterCopilotEnabled || isArticlePageRedesignUnifiedUXEnabled,
      })}
    >
      <Box
        className={classNames(css.articleWrapperContainerNew, {
          [css.articleWrapperContainerNewKnownIssue]:
            article.type === ArticleType.KNOWN_ISSUE && !containsContent,
          [css.articleWrapperContainerCopilot]: isHelpCenterCopilotEnabled && !isArticlePageRedesignUnifiedUXEnabled,
          [css.articleWrapperContainerWithVisibleFeedback]:
            isHelpmateImprovedFeedbackEnabled && !isArticlePageRedesignUnifiedUXEnabled && shouldShowFeedback,
          [css.articleWrapperContainerRedesignUnifiedUX]:
            isArticlePageRedesignUnifiedUXEnabled,
        })}
        direction="vertical"
        ref={copilotArticleContentRef}
      >
        {isLoading && (
          <div className={showOnlyLoader ? css.loadingOverlay : undefined}>
            <Loader isSticky={true} />
          </div>
        )}
        {!showOnlyLoader && (
          <>
            {isHelpCenterCopilotEnabled &&
              !showLeftSideBar &&
              steps.length > 0 &&
              windowSize.width &&
              windowSize.width > 1215 && (
                <TopicsButton onClick={onTopicsButtonClick} />
              )}
            {isHelpCenterCopilotEnabled &&
              !showRightSideBar &&
              windowSize.width &&
              windowSize.width > 1215 && (
                <CopilotButton onClick={onCopilotButtonClick} />
              )}
            <Box
              direction="vertical"
              className={classNames(css.articleWrapper, css.articleWrapperNew, {
                [css.knownIssue]: article.type === ArticleType.KNOWN_ISSUE,
                [css.articleWrapperCopilot]: isHelpCenterCopilotEnabled && !isArticlePageRedesignUnifiedUXEnabled,
                [css.articleWrapperRedesignUnifiedUX]: isArticlePageRedesignUnifiedUXEnabled,
              })}
              align="center"
            >
              {isArticlePageRedesignUnifiedUXEnabled && (
                <Breadcrumbs breadcrumbItems={breadcrumbItems} />
              )}
              <Box
                direction="vertical"
                className={classNames(css.articleViewerWrapper, {
                  [css.articleViewerWrapperCopilot]: isHelpCenterCopilotEnabled,
                })}
              >
                {isHelpCenterCopilotEnabled && !isArticlePageRedesignUnifiedUXEnabled && (
                  <Breadcrumbs breadcrumbItems={breadcrumbItems} />
                )}
                <ArticleHeader
                  type={type}
                  title={title}
                  url={articleUrl}
                  resolution={resolution}
                  resolutionEta={resolutionEta as unknown as Long}
                  isRoadmapArticle={isRoadmapArticle}
                  readingTime={readingTimeInMinutes}
                  creationDate={article.creationDate}
                  onClickStatus={onClickStatus}
                  steps={steps}
                />
                {getWrongArticleDescription(articleId) && !isMobile && locale === LOCALES.EN && (
                  <WrongArticle
                    description={getWrongArticleDescription(articleId)!}
                    onClickFeedback={onWrongArticleFeedbackClick}
                    articleId={articleId}
                  />
                )}
                {containsContent ? (
                  <ArticleViewerWrapper
                    article={article}
                    onLinkClick={onLinkClick}
                    onTabClick={onTabClick}
                    maybeGetSelectedTabsFromURL={maybeGetSelectedTabsFromURL}
                  />
                ) : null}
              </Box>
            </Box>
            <ArticleFooter
              article={normalizeArticle(article)}
              isRoadmapArticle={showDisclaimerForFeatureRequest}
              hasNoContent={!containsContent}
              relatedArticles={relatedArticles}
              selfArticleRedirect={selfArticleRedirect}
            />
          </>
        )}
      </Box>
      {isHelpmateImprovedFeedbackEnabled && !isArticlePageRedesignUnifiedUXEnabled && !isLoading && (
        <Box
          className={classNames(css.stickyArticleFeedbackWrapper, {
            [css.visible]: shouldShowFeedback,
          })}
        >
          <ArticleFeedback
            articleId={articleId}
            hideFeedbackAfterDelay={hideFeedbackAfterDelay}
          />
        </Box>
      )}
    </div>
  ) : null;
};
const isLinkToArticle = (href: string, locale: string) =>
  href.includes(`/${locale}/article/`) || href.includes('/en/article/');
